import React, { useState, useEffect, useCallback } from "react";
import "./Main.css";
import MarkdownEditor from "../MarkdownEditor/MarkdownEditor";

const Main = () => {
  const [words, setWords] = useState(0);
  const [characters, setCharacters] = useState(0);
  const [sentences, setSentences] = useState(0);
  const [paragraphs, setParagraphs] = useState(0);
  const [readingTime, setReadingTime] = useState(0);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [activeTab, setActiveTab] = useState("text-tools");

  // Enhanced text analysis functions
  const analyzeText = (data) => {
    const trimmedData = data.trim();

    // Words
    if (trimmedData === "") {
      setWords(0);
    } else {
      const words = trimmedData.split(/\s+/);
      const filteredWords = words.filter((word) => word !== "");
      setWords(filteredWords.length);
    }

    // Characters (excluding spaces)
    const trimmedStr = data.replace(/\s/g, "");
    setCharacters(trimmedStr.length);

    // Sentences
    const sentences = data.split(/[.!?]+/).filter((s) => s.trim().length > 0);
    setSentences(sentences.length);

    // Paragraphs
    const paragraphs = data.split(/\n\s*\n/).filter((p) => p.trim().length > 0);
    setParagraphs(paragraphs.length);

    // Reading time (average 200 words per minute)
    const wordCount =
      trimmedData === ""
        ? 0
        : trimmedData.split(/\s+/).filter((w) => w !== "").length;
    setReadingTime(Math.ceil(wordCount / 200));
  };

  // Text transformation functions
  const toUppercase = useCallback(() => {
    const textarea = document.querySelector("textarea");
    const uppercaseData = textarea.value.toUpperCase();
    textarea.value = uppercaseData;
    analyzeText(uppercaseData);
  }, []);

  const toLowercase = useCallback(() => {
    const textarea = document.querySelector("textarea");
    const lowercaseData = textarea.value.toLowerCase();
    textarea.value = lowercaseData;
    analyzeText(lowercaseData);
  }, []);

  const toCapitalize = () => {
    const textarea = document.querySelector("textarea");
    const words = textarea.value.toLowerCase().split(" ");

    const capitalizedSentence = words
      .map((word) => {
        const capitalizedFirstLetter = word.charAt(0).toUpperCase();
        const restOfWord = word.slice(1);
        return capitalizedFirstLetter + restOfWord;
      })
      .join(" ");
    textarea.value = capitalizedSentence;
    analyzeText(capitalizedSentence);
  };

  const toTitleCase = () => {
    const textarea = document.querySelector("textarea");
    const text = textarea.value.toLowerCase();
    const titleCase = text.replace(/\b\w/g, (l) => l.toUpperCase());
    textarea.value = titleCase;
    analyzeText(titleCase);
  };

  const toSentenceCase = () => {
    const textarea = document.querySelector("textarea");
    const text = textarea.value.toLowerCase();
    const sentenceCase = text.replace(/(^\w|\.\s+\w)/g, (l) => l.toUpperCase());
    textarea.value = sentenceCase;
    analyzeText(sentenceCase);
  };

  const toReverse = useCallback(() => {
    const textarea = document.querySelector("textarea");
    const reversedStr = textarea.value.split("").reverse().join("");
    textarea.value = reversedStr;
    analyzeText(reversedStr);
  }, []);

  const toEncode = useCallback(() => {
    const textarea = document.querySelector("textarea");
    const encodedString = btoa(textarea.value);
    textarea.value = encodedString;
    analyzeText(encodedString);
  }, []);

  const toDecode = () => {
    const textarea = document.querySelector("textarea");
    try {
      const decodedString = atob(textarea.value);
      textarea.value = decodedString;
      analyzeText(decodedString);
    } catch (error) {
      alert("Invalid Base64 string!");
    }
  };

  // New text manipulation functions
  const toAlternatingCase = () => {
    const textarea = document.querySelector("textarea");
    const text = textarea.value;
    let result = "";
    for (let i = 0; i < text.length; i++) {
      result += i % 2 === 0 ? text[i].toLowerCase() : text[i].toUpperCase();
    }
    textarea.value = result;
    analyzeText(result);
  };

  const toInverseCase = () => {
    const textarea = document.querySelector("textarea");
    const text = textarea.value;
    const result = text
      .split("")
      .map((char) =>
        char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()
      )
      .join("");
    textarea.value = result;
    analyzeText(result);
  };

  const removeExtraSpaces = () => {
    const textarea = document.querySelector("textarea");
    const result = textarea.value.replace(/\s+/g, " ").trim();
    textarea.value = result;
    analyzeText(result);
  };

  const addLineNumbers = () => {
    const textarea = document.querySelector("textarea");
    const lines = textarea.value.split("\n");
    const numberedLines = lines.map((line, index) => `${index + 1}. ${line}`);
    const result = numberedLines.join("\n");
    textarea.value = result;
    analyzeText(result);
  };

  const toCopy = useCallback(async () => {
    const textarea = document.querySelector("textarea");
    try {
      await navigator.clipboard.writeText(textarea.value);
      showNotification("Text copied to clipboard!", "success");
    } catch (err) {
      textarea.select();
      document.execCommand("copy");
      showNotification("Text copied to clipboard!", "success");
    }
  }, []);

  const toClear = useCallback(() => {
    const textarea = document.querySelector("textarea");
    textarea.value = "";
    analyzeText("");
  }, []);

  // New utility functions
  const showNotification = (message, type = "info") => {
    // Create a simple notification system
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      border-radius: 0.5rem;
      color: white;
      font-weight: 500;
      z-index: 10000;
      animation: slideIn 0.3s ease-out;
      background: ${
        type === "success"
          ? "#10b981"
          : type === "error"
          ? "#ef4444"
          : "#3b82f6"
      };
    `;
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = "slideOut 0.3s ease-out forwards";
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  const textToSpeech = useCallback(() => {
    const text = document.querySelector("textarea").value;
    if (text.trim() === "") {
      showNotification("Please enter some text first!", "error");
      return;
    }

    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.8;
      utterance.pitch = 1;
      speechSynthesis.speak(utterance);
      showNotification("Playing text...", "info");
    } else {
      showNotification("Text-to-speech not supported in this browser", "error");
    }
  }, []);

  const analyzeWordFrequency = useCallback(() => {
    const text = document.querySelector("textarea").value.toLowerCase();
    const words = text.match(/\b\w+\b/g) || [];
    const frequency = {};

    words.forEach((word) => {
      frequency[word] = (frequency[word] || 0) + 1;
    });

    const sortedWords = Object.entries(frequency)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    if (sortedWords.length === 0) {
      showNotification("No words found to analyze", "error");
      return;
    }

    const result =
      "Top 10 Most Frequent Words:\n" +
      sortedWords.map(([word, count]) => `${word}: ${count}`).join("\n");

    document.querySelector("textarea").value = result;
    analyzeText(result);
  }, []);

  const sortLines = () => {
    const textarea = document.querySelector("textarea");
    const lines = textarea.value.split("\n");
    const sortedLines = lines.sort();
    const result = sortedLines.join("\n");
    textarea.value = result;
    analyzeText(result);
  };

  const removeDuplicateLines = () => {
    const textarea = document.querySelector("textarea");
    const lines = textarea.value.split("\n");
    const uniqueLines = [...new Set(lines)];
    const result = uniqueLines.join("\n");
    textarea.value = result;
    analyzeText(result);
  };

  // File operations
  const importFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".txt,.md,.json,.csv";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const textarea = document.querySelector("textarea");
          textarea.value = e.target.result;
          analyzeText(e.target.result);
          showNotification(
            `File "${file.name}" imported successfully!`,
            "success"
          );
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const exportAsHTML = () => {
    const text = document.querySelector("textarea").value;

    if (text.length <= 0) {
      showNotification("No text to export!", "error");
      return;
    }

    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Texify Elite Export</title>
    <style>
        body { font-family: 'Inter', sans-serif; max-width: 800px; margin: 0 auto; padding: 2rem; line-height: 1.6; }
        pre { background: #f5f5f5; padding: 1rem; border-radius: 8px; overflow-x: auto; }
    </style>
</head>
<body>
    <h1>Texify Elite Export</h1>
    <pre>${text.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</pre>
    <p><small>Exported on ${new Date().toLocaleString()}</small></p>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const link = document.createElement("a");
    link.download = "Texify-Elite-Export.html";
    link.href = window.URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showNotification("HTML file exported successfully!", "success");
  };

  const exportAsJSON = () => {
    const text = document.querySelector("textarea").value;

    if (text.length <= 0) {
      showNotification("No text to export!", "error");
      return;
    }

    const data = {
      text: text,
      statistics: {
        characters: characters,
        words: words,
        sentences: sentences,
        paragraphs: paragraphs,
        readingTime: readingTime,
      },
      exportedAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const link = document.createElement("a");
    link.download = "Texify-Elite-Export.json";
    link.href = window.URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showNotification("JSON file exported successfully!", "success");
  };

  const downloadTextAsFile = useCallback(() => {
    const text = document.querySelector("textarea").value;

    if (text.length <= 0) {
      showNotification("No text to download!", "error");
      return;
    }

    const blob = new Blob([text], { type: "text/plain" });
    const link = document.createElement("a");
    link.download = "Texify-Elite.txt";
    link.href = window.URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showNotification("File downloaded successfully!", "success");
  }, []);

  const removeWhitespace = () => {
    const textarea = document.querySelector("textarea");
    const transformedText = textarea.value.replace(/\s+/g, "");
    textarea.value = transformedText;
    analyzeText(transformedText);
  };

  const removeSpecialCharacters = () => {
    const textarea = document.querySelector("textarea");
    const transformedText = textarea.value.replace(/[^a-zA-Z0-9 ]/g, "");
    textarea.value = transformedText;
    analyzeText(transformedText);
  };

  const replaceCharacters = () => {
    const textarea = document.querySelector("textarea");
    const oldChar = prompt("Enter the character to be replaced:");
    const newChar = prompt("Enter the new character:");

    if (oldChar && newChar !== null) {
      const transformedText = textarea.value.split(oldChar).join(newChar);
      textarea.value = transformedText;
      analyzeText(transformedText);
    }
  };

  const generateText = () => {
    const wordCount = parseInt(prompt("Enter the number of words:"), 10);

    if (isNaN(wordCount) || wordCount <= 0) {
      showNotification("Please enter a valid number of words.", "error");
      return;
    }

    const words = generateLoremIpsumWords(wordCount);
    const textarea = document.querySelector("textarea");
    textarea.value = words.join(" ");
    analyzeText(words.join(" "));
  };

  const generateLoremIpsumWords = (count) => {
    const loremIpsumText =
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.";
    const loremIpsumWords = loremIpsumText.split(" ");

    let randomWords = [];
    for (let i = 0; i < count; i++) {
      const randomIndex = Math.floor(Math.random() * loremIpsumWords.length);
      randomWords.push(loremIpsumWords[randomIndex]);
    }
    return randomWords;
  };
  const renderActiveTab = () => {
    switch (activeTab) {
      case "text-tools":
        return renderTextTools();
      case "markdown-editor":
        return <MarkdownEditor />;
      default:
        return renderTextTools();
    }
  };

  const renderTextTools = () => (
    <>
      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div
          className="shortcuts-modal"
          onClick={() => setShowShortcuts(false)}
        >
          <div
            className="shortcuts-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shortcuts-header">
              <h3>⌨️ Keyboard Shortcuts</h3>
              <button
                className="close-btn"
                onClick={() => setShowShortcuts(false)}
              >
                ×
              </button>
            </div>
            <div className="shortcuts-grid">
              <div className="shortcut-item">
                <kbd>Ctrl + B</kbd>
                <span>Uppercase</span>
              </div>
              <div className="shortcut-item">
                <kbd>Ctrl + L</kbd>
                <span>Lowercase</span>
              </div>
              <div className="shortcut-item">
                <kbd>Ctrl + C</kbd>
                <span>Copy Text</span>
              </div>
              <div className="shortcut-item">
                <kbd>Ctrl + D</kbd>
                <span>Download</span>
              </div>
              <div className="shortcut-item">
                <kbd>Ctrl + K</kbd>
                <span>Clear Text</span>
              </div>
              <div className="shortcut-item">
                <kbd>Ctrl + R</kbd>
                <span>Reverse Text</span>
              </div>
              <div className="shortcut-item">
                <kbd>Ctrl + E</kbd>
                <span>Encode Base64</span>
              </div>
              <div className="shortcut-item">
                <kbd>Ctrl + F</kbd>
                <span>Word Frequency</span>
              </div>
              <div className="shortcut-item">
                <kbd>Ctrl + S</kbd>
                <span>Text to Speech</span>
              </div>
              <div className="shortcut-item">
                <kbd>Ctrl + ?</kbd>
                <span>Show Shortcuts</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="textarea-container">
        <div className="textarea-header">
          <h3>📝 Text Editor</h3>
          <button
            className="shortcuts-btn"
            onClick={() => setShowShortcuts(true)}
            title="Show keyboard shortcuts"
          >
            ⌨️ Shortcuts
          </button>
        </div>
        <div className="textarea-div">
          <textarea
            id="textarea"
            className="textarea"
            placeholder="Enter your text here to get started... Try some keyboard shortcuts!"
            onChange={() => {
              const data = document.querySelector("textarea").value;
              analyzeText(data);
            }}
            spellCheck="true"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
          />
        </div>
      </div>

      <div className="button-container">
        <div className="button-grid">
          {/* Text Case Transformations */}
          <div className="button-group">
            <h3>📝 Case Transformations</h3>
            <div className="button-row">
              <button className="my-button btn-primary" onClick={toUppercase}>
                UPPERCASE
              </button>
              <button className="my-button btn-primary" onClick={toLowercase}>
                lowercase
              </button>
              <button className="my-button btn-primary" onClick={toCapitalize}>
                Capitalize
              </button>
              <button className="my-button btn-primary" onClick={toTitleCase}>
                Title Case
              </button>
              <button
                className="my-button btn-primary"
                onClick={toSentenceCase}
              >
                Sentence case
              </button>
            </div>
          </div>

          {/* Text Manipulation */}
          <div className="button-group">
            <h3>🔄 Text Manipulation</h3>
            <div className="button-row">
              <button className="my-button btn-secondary" onClick={toReverse}>
                Reverse
              </button>
              <button
                className="my-button btn-secondary"
                onClick={toAlternatingCase}
              >
                AlTeRnAtE
              </button>
              <button
                className="my-button btn-secondary"
                onClick={toInverseCase}
              >
                InVeRsE
              </button>
              <button
                className="my-button btn-secondary"
                onClick={removeExtraSpaces}
              >
                Fix Spaces
              </button>
              <button
                className="my-button btn-secondary"
                onClick={addLineNumbers}
              >
                Add Numbers
              </button>
            </div>
          </div>

          {/* Encoding & Decoding */}
          <div className="button-group">
            <h3>🔐 Encoding & Decoding</h3>
            <div className="button-row">
              <button className="my-button btn-warning" onClick={toEncode}>
                Encode Base64
              </button>
              <button className="my-button btn-warning" onClick={toDecode}>
                Decode Base64
              </button>
            </div>
          </div>

          {/* Text Analysis */}
          <div className="button-group">
            <h3>📊 Text Analysis</h3>
            <div className="button-row">
              <button
                className="my-button btn-success"
                onClick={analyzeWordFrequency}
              >
                Word Frequency
              </button>
              <button className="my-button btn-success" onClick={sortLines}>
                Sort Lines
              </button>
              <button
                className="my-button btn-success"
                onClick={removeDuplicateLines}
              >
                Remove Duplicates
              </button>
            </div>
          </div>

          {/* Text Processing */}
          <div className="button-group">
            <h3>🧹 Text Processing</h3>
            <div className="button-row">
              <button
                className="my-button btn-warning"
                onClick={removeWhitespace}
              >
                Remove Spaces
              </button>
              <button
                className="my-button btn-warning"
                onClick={removeSpecialCharacters}
              >
                Remove Special
              </button>
              <button
                className="my-button btn-warning"
                onClick={replaceCharacters}
              >
                Replace Text
              </button>
            </div>
          </div>

          {/* File Operations */}
          <div className="button-group">
            <h3>📁 File Operations</h3>
            <div className="button-row">
              <button className="my-button btn-primary" onClick={importFile}>
                📥 Import
              </button>
              <button
                className="my-button btn-success"
                onClick={downloadTextAsFile}
              >
                💾 TXT
              </button>
              <button className="my-button btn-success" onClick={exportAsHTML}>
                🌐 HTML
              </button>
              <button className="my-button btn-success" onClick={exportAsJSON}>
                📊 JSON
              </button>
            </div>
          </div>

          {/* Utilities */}
          <div className="button-group">
            <h3>🛠️ Utilities</h3>
            <div className="button-row">
              <button className="my-button btn-success" onClick={toCopy}>
                📋 Copy
              </button>
              <button className="my-button btn-success" onClick={textToSpeech}>
                🔊 Speak
              </button>
              <button className="my-button btn-warning" onClick={generateText}>
                ✨ Generate
              </button>
              <button className="my-button btn-error" onClick={toClear}>
                🗑️ Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <section className="text-summary">
        <h2>📈 Text Statistics</h2>
        <div className="summary-grid">
          <div className="summary-item">
            <span>Characters (no spaces)</span>
            <span>{characters}</span>
          </div>
          <div className="summary-item">
            <span>Words</span>
            <span>{words}</span>
          </div>
          <div className="summary-item">
            <span>Sentences</span>
            <span>{sentences}</span>
          </div>
          <div className="summary-item">
            <span>Paragraphs</span>
            <span>{paragraphs}</span>
          </div>
          <div className="summary-item">
            <span>Reading Time</span>
            <span>{readingTime} min</span>
          </div>
        </div>
      </section>
    </>
  );

  const redirectToNotecraft = () => {
    window.open("https://notecraft-elite.vercel.app/", "_blank");
  };


  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case "b":
            e.preventDefault();
            toUppercase();
            break;
          case "l":
            e.preventDefault();
            toLowercase();
            break;
          case "c":
            if (!e.shiftKey) {
              e.preventDefault();
              toCopy();
            }
            break;
          case "d":
            e.preventDefault();
            downloadTextAsFile();
            break;
          case "k":
            e.preventDefault();
            toClear();
            break;
          case "r":
            e.preventDefault();
            toReverse();
            break;
          case "e":
            e.preventDefault();
            toEncode();
            break;
          case "f":
            e.preventDefault();
            analyzeWordFrequency();
            break;
          case "s":
            e.preventDefault();
            textToSpeech();
            break;
          case "?":
            e.preventDefault();
            setShowShortcuts(!showShortcuts);
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    showShortcuts,
    toUppercase,
    toLowercase,
    toCopy,
    downloadTextAsFile,
    toClear,
    toReverse,
    toEncode,
    analyzeWordFrequency,
    textToSpeech,
  ]);

  return (
    <div className="main-section">
      {/* Tab Navigation */}
      <div className="tab-navigation">
        <div className="tab-container">
          <button
            className={`tab-button ${
              activeTab === "text-tools" ? "active" : ""
            }`}
            onClick={() => setActiveTab("text-tools")}
          >
            📝 Text Tools
          </button>
          <button
            className={`tab-button ${
              activeTab === "markdown-editor" ? "active" : ""
            }`}
            onClick={() => setActiveTab("markdown-editor")}
          >
            📝 Markdown Editor
          </button>
          <button className="tab-button" onClick={redirectToNotecraft}>
            ✨ NoteCraft Elite
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">{renderActiveTab()}</div>
    </div>
  );
};

export default Main;
